/**
 * ONE TAP — hold-TAB scoreboard.
 *
 * KILLS and DEATHS are table stakes. CATCHES and INTERCEPTS get the same column
 * width, the same type size and a highlighted TEAMWORK group header, because in
 * a game with one gun per team those two numbers are the ones that actually
 * describe whether you played well.
 */

import { ROUNDS_TO_WIN, TEAM_A, TEAM_B, TEAM_NAMES } from '@shared/constants';
import type { PlayerState, WorldState } from '@shared/types';
import { HealthState } from '@shared/types';
import { esc, teamColor } from './settings';

/** `ParentNode` is ambiguous here — @cloudflare/workers-types declares a global one
 *  for HTMLRewriter — so this takes the structural shape it actually needs. */
type Queryable = { querySelector(selectors: string): Element | null };

const q = <T extends HTMLElement>(root: Queryable, sel: string): T => {
  const found = root.querySelector(sel);
  if (!found) throw new Error(`scoreboard: missing element ${sel}`);
  return found as T;
};

function teamBlock(team: number, side: 'a' | 'b'): string {
  return `
  <section class="ot-sb__team ot-sb__team--${side}" data-team="${team}">
    <header class="ot-sb__teamhead">
      <h3 class="ot-sb__teamname" id="ot-sb-name-${side}">TEAM ${esc(TEAM_NAMES[team] ?? '')}</h3>
      <span class="ot-sb__teamscore" id="ot-sb-score-${side}">0</span>
    </header>
    <div class="ot-sb__cols" aria-hidden="true">
      <span class="ot-sb__col ot-sb__col--name">PLAYER</span>
      <span class="ot-sb__col">K</span>
      <span class="ot-sb__col">D</span>
      <span class="ot-sb__col ot-sb__col--team">CATCH</span>
      <span class="ot-sb__col ot-sb__col--team">INTCPT</span>
      <span class="ot-sb__col">PING</span>
    </div>
    <ul class="ot-sb__rows" id="ot-sb-rows-${side}" aria-labelledby="ot-sb-name-${side}"></ul>
    <footer class="ot-sb__totals" id="ot-sb-tot-${side}"></footer>
  </section>`;
}

const SB_HTML = `
<div class="ot-sb__box" role="dialog" aria-label="Scoreboard">
  <header class="ot-sb__head">
    <span class="ot-sb__title">SCOREBOARD</span>
    <span class="ot-sb__meta" id="ot-sb-meta"></span>
    <!-- No legend. "CATCHES and INTERCEPTS are the teamwork stats — they win
         rounds" was design documentation aimed at the developer, not the player
         (DESIGN.md §8). The columns already carry the amber billing; a player who
         has caught a gun knows exactly what the number counts. -->
  </header>
  <div class="ot-sb__body">
    ${teamBlock(TEAM_A, 'a')}
    <div class="ot-sb__divider" aria-hidden="true"></div>
    ${teamBlock(TEAM_B, 'b')}
  </div>
</div>`;

interface RowRefs {
  root: HTMLLIElement;
  cells: HTMLElement[];
  last: string[];
}

const CELL_COUNT = 6;

export class Scoreboard {
  readonly element: HTMLElement;

  private readonly rowsA: HTMLElement;
  private readonly rowsB: HTMLElement;
  private readonly scoreAEl: HTMLElement;
  private readonly scoreBEl: HTMLElement;
  private readonly totA: HTMLElement;
  private readonly totB: HTMLElement;
  private readonly meta: HTMLElement;

  private readonly rows = new Map<string, RowRefs>();
  private visible = false;
  private pinned = false;

  constructor() {
    const el = document.createElement('section');
    el.className = 'ot-screen ot-sb';
    el.hidden = true;
    el.innerHTML = SB_HTML;
    this.element = el;

    this.rowsA = q(el, '#ot-sb-rows-a');
    this.rowsB = q(el, '#ot-sb-rows-b');
    this.scoreAEl = q(el, '#ot-sb-score-a');
    this.scoreBEl = q(el, '#ot-sb-score-b');
    this.totA = q(el, '#ot-sb-tot-a');
    this.totB = q(el, '#ot-sb-tot-b');
    this.meta = q(el, '#ot-sb-meta');

    q(el, '#ot-sb-name-a').style.setProperty('--c', teamColor(TEAM_A));
    q(el, '#ot-sb-name-b').style.setProperty('--c', teamColor(TEAM_B));
  }

  isVisible(): boolean {
    return this.visible || this.pinned;
  }

  /** Held-TAB visibility. */
  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    this.applyVisibility();
  }

  /** Latched open — used at match end, where nobody should have to hold a key. */
  setPinned(v: boolean): void {
    if (v === this.pinned) return;
    this.pinned = v;
    this.applyVisibility();
  }

  update(w: WorldState, localId: string): void {
    if (!this.isVisible()) return;

    const m = w.match;
    this.scoreAEl.textContent = String(m.scoreA);
    this.scoreBEl.textContent = String(m.scoreB);
    this.meta.textContent = `ROUND ${m.round} · FIRST TO ${ROUNDS_TO_WIN}`;

    const teams: [PlayerState[], PlayerState[]] = [[], []];
    for (const id in w.players) {
      const p = w.players[id];
      if (!p) continue;
      teams[p.team === TEAM_B ? 1 : 0].push(p);
    }

    const seen = new Set<string>();
    for (let t = 0; t < 2; t++) {
      const list = teams[t]!;
      // Kills first, then the teamwork stats — the sort agrees with the billing.
      list.sort(
        (a, b) =>
          b.kills - a.kills ||
          b.catches + b.intercepts - (a.catches + a.intercepts) ||
          a.deaths - b.deaths ||
          a.name.localeCompare(b.name),
      );

      const host = t === 0 ? this.rowsA : this.rowsB;
      let totK = 0;
      let totC = 0;
      let totI = 0;

      list.forEach((p, i) => {
        seen.add(p.id);
        totK += p.kills;
        totC += p.catches;
        totI += p.intercepts;

        const row = this.rowFor(p, localId);
        const at = host.children[i];
        if (at !== row.root) host.insertBefore(row.root, at ?? null);
      });

      const tot = t === 0 ? this.totA : this.totB;
      const text = `${totK} KILLS · ${totC} CATCHES · ${totI} INTERCEPTS`;
      if (tot.textContent !== text) tot.textContent = text;
    }

    for (const [id, row] of this.rows) {
      if (!seen.has(id)) {
        row.root.remove();
        this.rows.delete(id);
      }
    }
  }

  dispose(): void {
    this.element.remove();
  }

  // -------------------------------------------------------------------------

  private applyVisibility(): void {
    const on = this.isVisible();
    this.element.hidden = !on;
    this.element.classList.toggle('is-open', on);
  }

  private rowFor(p: PlayerState, localId: string): RowRefs {
    let row = this.rows.get(p.id);
    if (!row) {
      const root = document.createElement('li');
      root.className = 'ot-sb__row';
      root.innerHTML =
        `<span class="ot-sb__cell ot-sb__cell--name"></span>` +
        `<span class="ot-sb__cell"></span>` +
        `<span class="ot-sb__cell"></span>` +
        `<span class="ot-sb__cell ot-sb__cell--team"></span>` +
        `<span class="ot-sb__cell ot-sb__cell--team"></span>` +
        `<span class="ot-sb__cell ot-sb__cell--ping"></span>`;
      const cells = Array.from(root.querySelectorAll<HTMLElement>('.ot-sb__cell'));
      row = { root, cells, last: new Array<string>(CELL_COUNT).fill('') };
      root.style.setProperty('--c', teamColor(p.team));
      this.rows.set(p.id, row);
    }

    row.root.classList.toggle('is-you', p.id === localId);
    row.root.classList.toggle('is-bot', p.bot);
    row.root.classList.toggle('is-dead', p.healthState === HealthState.Dead);
    row.root.style.setProperty('--c', teamColor(p.team));

    const values = [
      p.name + (p.bot ? ' · BOT' : ''),
      String(p.kills),
      String(p.deaths),
      String(p.catches),
      String(p.intercepts),
      p.bot ? '—' : `${Math.round(p.latencyMs)}`,
    ];

    for (let i = 0; i < CELL_COUNT; i++) {
      if (row.last[i] !== values[i]) {
        row.last[i] = values[i]!;
        row.cells[i]!.textContent = values[i]!;
      }
    }
    row.cells[5]!.dataset.q = p.bot ? 'bot' : p.latencyMs < 60 ? 'good' : p.latencyMs < 130 ? 'ok' : 'bad';

    return row;
  }
}
